import React, {useContext, useEffect, useRef, useState} from 'react';
import withLayout from '../../components/Layout/WithLayout';
import DefaultLayout from '../../components/Layout/Default';
import {FormSteps} from '../../config/formSteps';
import {TollRegistrationContext} from '../../context/ApplicationContext';
import {useHistory} from 'react-router-dom';
import routes from '../../routes';
import {InputValueAndError} from '../../types/InputValueAndError';
import TollReliefConfig from '../../config/TollReliefConfig';
import {collectErrorSummaryErrors} from '../../utils/helpers';
import {eligibilityApi, getSoleTraderBusinessDetailsFailures, SoleTraderFailure,} from '../../services/api-services';
import {Button, Col, ComponentLoader, ErrorSummary, FormRadioGroup, InPageAlert, Row,} from '@snsw-gel/react';
import {BusinessSearch, ButtonsContainer} from '@gaas/gaas-react-component-library';
import ApiErrorAlert from './ApiErrorAlert';
import {BusinessEntry} from '../../types/Gaas';
import useTrRegistrationForm from '../../hooks/useTrRegistrationForm';
import TollAccountType from '../../types/tollAccountType';
import {userInfo} from 'src/utils/sessionStorage';
import { EventData, generateEventData } from 'src/utils/ga-page-tracking';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

// TODO remove this file after Tr2 decom
const SoleTraderBusinessDetailsPage: React.FC = () => {
    const {productData, setProductData} = useContext(TollRegistrationContext);
    const trTransactionForm = useTrRegistrationForm();
    const tollAccountType = productData.tollAccountType;
    const history = useHistory();

    const { isFeatureEnabled, isFeatureQueryLoading } = useBaseFeature();
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean>(false);

    const [isRideShareOrTaxi, setIsRideShareOrTaxi] =
        useState<InputValueAndError<boolean>>(
            new InputValueAndError<boolean>(null, (input) => {
                if(input.hasValue()) {
                    return [input.value === false, 'Business is not eligible for toll relief'];
                } else {
                    return [true];
                }
            })
        );

    const [abn, setAbn] =
        useState<InputValueAndError<string | null>>(
            new InputValueAndError<string | null>(null, (input) => {
                return [input.hasValue()]
            })
        );
    const [businessName, setBusinessName] = useState<string>('');

    /** For tracking when elig api is to be called. And displaying component loader. */
    const [isEligibilityApiInProgress, setIsEligibilityApiInProgress] = useState(false);

    /** For api errors */
    const [apiErrorReason, setApiErrorReason] = useState<SoleTraderFailure | undefined>(undefined);

    /** If the user has tried to continue. Used for displaying error summary or not. */
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    /** Holds UI errors (not for bad api responses) */
    const formErrors = collectErrorSummaryErrors([
        {
            inputValue: isRideShareOrTaxi,
            anchor: TollReliefConfig.anchors.rideshare,
            message: 'Is your business operating as a rideshare or taxi business is required.',
        },
        {
            inputValue: abn,
            anchor: TollReliefConfig.anchors.abn,
            message: 'Enter business name, ABN or ACN',
        },
    ]);
    const errorSummaryRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if(!isFeatureQueryLoading && isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION)) {
            setIsTr2Decommissioned(true);
            history.push(routes.manageTollAccount.path);
            return;
        }
    }, [history, isFeatureQueryLoading, isFeatureEnabled]);

    const userInfoObj = userInfo();
    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            // Simulating data for the event
            const eventData: EventData = {
                page_title: 'Link toll account',
                transaction_info: {
                    transaction_step: 'Sole trader details',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    // Require toll account type to exist
    useEffect(() => {
        if (tollAccountType === undefined) {
            history.push(routes.manageTollAccount.path);
        }
    }, []);

    const onSelectBusiness = () => (businessEntry: BusinessEntry) => {
        setAbn(abn.withValue(businessEntry.abn).validated());
        setBusinessName(businessEntry.businessName);

    }

    function onChangeRideshare(value: boolean) {
        // @ts-ignore
        setIsRideShareOrTaxi(isRideShareOrTaxi.withValue((value === 'true')).validated());
        setAbn(abn.withValue(null));
    }

    function onContinue() {
        if (isEligibilityApiInProgress) {
            return;
        }

        let valid = true;
        setHasAttemptedContinue(true);
        // Validate the ui fields
        if (isRideShareOrTaxi.hasNoValue()) {
            setIsRideShareOrTaxi(isRideShareOrTaxi.withError());
            valid = false;
        }

        if(isRideShareOrTaxi.hasValue() && isRideShareOrTaxi.value === false) {
            if(abn.hasNoValue()) {
                setAbn(abn.withError());
                valid = false;
            }
        } else {
            valid = false;
        }

        if(!valid) {
            errorSummaryRef.current?.focus();
        } else {
            const newProductData = {
                ...productData, soleTraderDetailData: {
                    abn: abn.value!,
                    isRideShareOrTaxi: isRideShareOrTaxi.value!,
                    businessName: businessName
                },
            }
            setProductData(newProductData);
            setIsEligibilityApiInProgress(true);

            eligibilityApi(trTransactionForm.data(newProductData)).then(
                (eligibilityFailures) => {
                    const responsesWeCareAbout = getSoleTraderBusinessDetailsFailures(eligibilityFailures);

                    if (responsesWeCareAbout.length === 0) { // success
                        history.push(routes.supportingDocumentsPage.path);
                    } else { // Failure
                        // We only display one error
                        setApiErrorReason(responsesWeCareAbout[0]);
                    }
                }
            )
                .finally(() => {
                    setIsEligibilityApiInProgress(false);
                });
        }
    }

    function onBack() {
        const backRoute = productData.tollAccountType === TollAccountType.Business ?
            routes.businessTollAccountDetails.path : routes.mixedTollAccountDetails.path;
        history.push(backRoute);
    }

    return (
        <>
            <ComponentLoader active={isEligibilityApiInProgress || isFeatureQueryLoading || isTr2Decommissioned}
                             fullPage label={TollReliefConfig.labels.componentLoader}/>

            {apiErrorReason !== undefined && <ApiErrorAlert errorReason={apiErrorReason}/>}

            {/* Error summary - for frontend errors */}
            {hasAttemptedContinue && formErrors.length !== 0 && <ErrorSummary errors={formErrors} ref={errorSummaryRef}/>}

            <p>All fields to be completed unless marked (optional)</p>
            <h2>Sole trader details</h2>
            <p>
                Give false or misleading information to a public authority is a serious offence under the <i>Crimes Act 1900 (NSW)</i>.
            </p>
            <FormRadioGroup
                id={TollReliefConfig.anchors.rideshare}
                name='rideshare'
                legend='Is your business operating as a rideshare or taxi business?'
                hasError={isRideShareOrTaxi.hasError}
                errorMessage={''}
                options={[
                    {value: false, label: 'No'},
                    {value: true, label: 'Yes'},
                ]}
                onChange={(value: boolean) => {
                    onChangeRideshare(value)
                }}
                value={isRideShareOrTaxi.value}
                vertical
            />

            {isRideShareOrTaxi.value === false &&
                <Row>
                    <Col span={6}>
                        <BusinessSearch
                            name='abnLookup'
                            label='Business name, ABN or ACN'
                            onSelect={onSelectBusiness()}
                            initialValue={abn.value ?? ''}
                            hasError={abn.hasError}
                            errorMessage={'No business name, 9 digit ACN or 11 digit ABN was entered'}
                        />
                    </Col>
                </Row>
            }

            {isRideShareOrTaxi.value === true &&
                <InPageAlert
                    variant='error'
                    title='Business is not eligible for toll relief'
                    id='businessNotEligibleAlert'
                    role='alert'
                >
                    <p>To be eligible, businesses must not operate rideshare or taxi services.</p>
                </InPageAlert>
            }
            <ButtonsContainer>
                <Button
                    onClick={onContinue}
                    id='continueButtonSoleTraderBusiness'
                >
                    Continue
                </Button>
            </ButtonsContainer>

            <Button
                onClick={onBack}
                theme='back'
                id='backButton'
            >
                Back
            </Button>
        </>
    );
};

export default withLayout(SoleTraderBusinessDetailsPage, DefaultLayout, {
    step: FormSteps.SoleTraderDetails,
    headerTitle: 'Link toll account',
});
